import type { ConnectError } from '@connectrpc/connect'
import { createConnectQueryKey, useMutation } from '@connectrpc/connect-query'
import { ResolvePlanCounterpartyMismatchResult } from '@ebalance/proto/bffuser/v1/submission_management_model_pb'
import {
  getPlanCounterpartyMismatch,
  getSubmissionDashboard,
  resolvePlanCounterpartyMismatch,
} from '@ebalance/proto/bffuser/v1/submission_management_service-SubmissionManagementService_connectquery'
import type { ResolvePlanCounterpartyMismatchResponse } from '@ebalance/proto/bffuser/v1/submission_management_service_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import {
  type PlanTarget,
  toPlanTargetRequest,
} from '~/pages/ProcurementPlanDetail/models/planTarget'

/**
 * 取引相手間不整合の解消 mutation hook (AC-PSM-064 / 065)。
 *
 * - RESOLVED: success toast、read と提出管理 dashboard を invalidate、`onResolved`。
 * - STALE: 記録されていない。read を再取得し、未解消が 0 件なら `onResolved` で閉じる。
 *   0 件でなければ呼び出し側は開いたまま最新一覧を表示する。
 * - error: error toast のみ (自動再試行しない。ダイアログは閉じない)。
 */
export function useResolvePlanCounterpartyMismatch(
  target: PlanTarget,
  onResolved: () => void,
): {
  resolve: (resolutionToken: string) => void
  isResolving: boolean
} {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const showSuccess = useSuccessToast()
  const showError = useErrorToast()

  const { mutate, isPending } = useMutation(resolvePlanCounterpartyMismatch, {
    onSuccess: async (
      res: ResolvePlanCounterpartyMismatchResponse,
    ): Promise<void> => {
      if (res.result === ResolvePlanCounterpartyMismatchResult.RESOLVED) {
        showSuccess({
          title: t(
            'page:procurementPlanDetail.counterpartyMismatch.successTitle',
          ),
        })
        onResolved()
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: createConnectQueryKey({
              schema: getPlanCounterpartyMismatch,
              cardinality: 'finite',
            }),
          }),
          queryClient.invalidateQueries({
            queryKey: createConnectQueryKey({
              schema: getSubmissionDashboard,
              cardinality: 'finite',
            }),
          }),
        ])
        return
      }
      // STALE: 記録されていない。再取得して最新の一覧に差し替える (開閉は呼び出し側が hasUnresolved で決める)。
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getPlanCounterpartyMismatch,
          cardinality: 'finite',
        }),
      })
    },
    onError: (_err: ConnectError): void => {
      showError({
        title: t('page:procurementPlanDetail.counterpartyMismatch.errorTitle'),
      })
    },
  })

  return {
    resolve: (resolutionToken: string): void => {
      mutate({ ...toPlanTargetRequest(target), resolutionToken })
    },
    isResolving: isPending,
  }
}
